import _ from 'lodash';

import { useAuthStore } from '@/stores';

/**
 * Whether the caller may step into a SUPER USER.
 *
 * Normally nobody may: it is not support work, and it puts one member of staff
 * behind another's identity. The exception is whoever administers the team -
 * holding `platform:super-users` already means being able to promote a puppet
 * account and grant it anything, so refusing the session on top of that is
 * theatre.
 *
 * A CONFINED holder is still refused, and that is the half that carries weight:
 * for them it would be a straight escalation, because a newly promoted super
 * user starts unrestricted.
 */
const mayMimicSuperUsers = (auth) =>
    auth.platformRightKeys.indexOf('platform:super-users') !== -1 && auth.reachableCmsIds === null;

/**
 * Whether a mimic control may be offered at all.
 *
 * Mirrors LoginController::loginAs one to one, because a button that is only
 * refused after the click is worse than no button:
 *
 *   - the caller needs `platform:mimic`;
 *   - yourself is never a target;
 *   - a SUPER USER is a target only under the rule above;
 *   - a confined super user may only enter an account whose reach is inside
 *     their own - taking over somebody who administers A and B hands over B
 *     as well.
 *
 * The reach is NOT recomputed here. The server sends it on the authenticated
 * user (`reachable_cms_ids`, null meaning "all"), so the button and the guard
 * cannot drift apart.
 */
export const canMimicUser = (user) => {
    const auth = useAuthStore();

    if (!auth.mayMimic) return false;

    const targetId = _.get(user, 'id');
    if (targetId && String(targetId) === String(_.get(auth, 'user.id'))) return false;

    if (_.get(user, 'super_user') === true && !mayMimicSuperUsers(auth)) return false;

    const reachable = auth.reachableCmsIds;

    if (reachable === null) return true;

    // The SAME set the server reads (User::governingAdministrators): the
    // account list sends verified rows, and an invitation nobody accepted
    // grants no reach.
    const targets = (_.get(user, 'administrators') || [])
        .map((administrator) => _.get(administrator, 'cms.id'))
        .filter((id) => id)
        .map((id) => String(id));

    if (!targets.length) return false;

    return targets.every((id) => reachable.indexOf(id) !== -1);
};

/**
 * A website row: the account that OWNS it is the one that would be entered.
 *
 * So the question is never "is this website in reach" - it is the same question
 * canMimicUser asks about that owner. Checking only the clicked website offered
 * a button whenever that one site was in reach, while the server refused
 * because the owner also administers others; entering them hands those over
 * too.
 */
export const canMimicWebsite = (website) => {
    const owner = _.get(website, 'owner.user');

    if (!owner) return false;

    return canMimicUser(owner);
};
